/**
 * Staff Inbox POC - Inter-Agent Event Viewer
 * Proof of concept for Event Bus functionality
 * Shows events from Bitsy/Sage that require staff action
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Bell, 
  CheckCircle, 
  Clock, 
  Sparkles, 
  TrendingUp, 
  Zap,
  RefreshCw,
  Filter,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export default function StaffInboxPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDepartment, setFilterDepartment] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    fetchEvents();
    
    if (autoRefresh) {
      const interval = setInterval(fetchEvents, 30000);
      return () => clearInterval(interval);
    }
  }, [filterStatus, filterDepartment, autoRefresh]);

  const fetchEvents = async () => {
    try {
      const params = new URLSearchParams({
        hotel_id: 'hotel_demo_001', // TODO: Get from user context
        limit: '50'
      });
      
      if (filterStatus) params.append('status', filterStatus);
      if (filterDepartment) params.append('target_department', filterDepartment);
      
      const response = await fetch(`${BACKEND_URL}/api/events/stream?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/events/stats?hotel_id=hotel_demo_001&days=7`
      );
      const data = await response.json();
      
      if (data.success) {
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const acknowledgeEvent = async (eventId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/events/${eventId}/acknowledge`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ acknowledged_by: 'staff_poc' })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Event acknowledged');
        fetchEvents();
      }
    } catch (error) {
      console.error('Failed to acknowledge event:', error);
      toast.error('Failed to acknowledge event');
    }
  };

  const completeEvent = async (eventId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/events/${eventId}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            completed_by: 'staff_poc',
            result_data: {
              action_taken: 'Task completed via POC inbox',
              timestamp: new Date().toISOString()
            }
          })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Event completed! Analytics event triggered.');
        fetchEvents();
      }
    } catch (error) {
      console.error('Failed to complete event:', error);
      toast.error('Failed to complete event');
    }
  };

  const getEventIcon = (eventType) => {
    if (eventType.startsWith('loyalty')) return Sparkles;
    if (eventType.startsWith('ops')) return Clock;
    if (eventType.startsWith('strategy')) return TrendingUp;
    return Bell;
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'urgent': return 'bg-[hsl(38_92%_55%)]/15 text-[hsl(38_92%_55%)] border-[hsl(38_92%_55%)]/25';
      case 'alert': return 'bg-primary/10 text-primary border-primary/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-[hsl(var(--status-clean))]/10 text-[hsl(var(--status-clean))] border-[hsl(var(--status-clean))]/20">Completed</Badge>;
      case 'acknowledged':
        return <Badge className="bg-primary/10 text-primary border-primary/20">In Progress</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const pendingEvents = events.filter(e => e.status === 'pending');
  const acknowledgedEvents = events.filter(e => e.status === 'acknowledged');
  const completedEvents = events.filter(e => e.status === 'completed');

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl heading-font tracking-tight">
              Staff Inbox <span className="text-muted-foreground text-base">(POC)</span>
            </h1>
            <p className="text-muted-foreground mt-2">
              Inter-Agent Event Bus • Real-time coordination between Bitsy, Marina, and Sage
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEvents}
              data-testid="refresh-events-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            
            <Button
              variant={autoRefresh ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="auto-refresh-toggle"
            >
              <Zap className="w-4 h-4 mr-2" />
              {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl heading-font tabular-nums text-primary">{stats.total_events}</p>
                <p className="text-sm text-muted-foreground mt-1">Total Events (7d)</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl heading-font tabular-nums text-[hsl(38_92%_55%)]">{pendingEvents.length}</p>
                <p className="text-sm text-muted-foreground mt-1">Pending</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl heading-font tabular-nums text-primary">{acknowledgedEvents.length}</p>
                <p className="text-sm text-muted-foreground mt-1">In Progress</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl heading-font tabular-nums text-[hsl(var(--status-clean))]">{completedEvents.length}</p>
                <p className="text-sm text-muted-foreground mt-1">Completed</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-2">
              <Badge
                variant={filterStatus === null ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterStatus(null)}
                data-testid="filter-all"
              >
                <Filter className="w-3 h-3 mr-1" />
                All Status
              </Badge>
              <Badge
                variant={filterStatus === 'pending' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterStatus(filterStatus === 'pending' ? null : 'pending')}
                data-testid="filter-pending"
              >
                Pending
              </Badge>
              <Badge
                variant={filterStatus === 'acknowledged' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterStatus(filterStatus === 'acknowledged' ? null : 'acknowledged')}
                data-testid="filter-acknowledged"
              >
                In Progress
              </Badge>
              <Badge
                variant={filterStatus === 'completed' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterStatus(filterStatus === 'completed' ? null : 'completed')}
                data-testid="filter-completed"
              >
                Completed
              </Badge>
              
              <div className="w-px h-6 bg-border mx-2" />
              
              <Badge
                variant={filterDepartment === 'housekeeping' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterDepartment(filterDepartment === 'housekeeping' ? null : 'housekeeping')}
                data-testid="filter-housekeeping"
              >
                🧹 Housekeeping
              </Badge>
              <Badge
                variant={filterDepartment === 'maintenance' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterDepartment(filterDepartment === 'maintenance' ? null : 'maintenance')}
                data-testid="filter-maintenance"
              >
                🔧 Maintenance
              </Badge>
              <Badge
                variant={filterDepartment === 'front_desk' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterDepartment(filterDepartment === 'front_desk' ? null : 'front_desk')}
                data-testid="filter-front-desk"
              >
                🏨 Front Desk
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Event List */}
        <div className="space-y-3">
          {events.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Archive className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="heading-font text-lg mb-2">No Events</h3>
                <p className="text-sm text-muted-foreground">
                  No events in the queue. The AI Organism is quiet... for now. 🧠
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchEvents}
                  className="mt-4"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </CardContent>
            </Card>
          ) : (
            events.map((event) => {
              const EventIcon = getEventIcon(event.event_type);
              const timeAgo = formatTimeAgo(event.created_at);
              
              return (
                <Card
                  key={event.event_id}
                  className={`transition-all duration-200 ${
                    event.status === 'pending' 
                      ? 'border-[hsl(38_92%_55%)]/30 bg-[hsl(38_92%_55%)]/5' 
                      : ''
                  }`}
                  data-testid={`event-card-${event.event_id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${getSeverityColor(event.severity)}`}>
                          <EventIcon className="w-5 h-5" />
                        </div>
                        
                        <div>
                          <CardTitle className="text-base heading-font">
                            {formatEventType(event.event_type)}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span className="mono">from {event.source_agent.toUpperCase()}</span>
                            <span>•</span>
                            <span>{timeAgo}</span>
                            <span>•</span>
                            <span className="mono">{event.event_id}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {getStatusBadge(event.status)}
                        <Badge variant="outline" className="capitalize">
                          {event.severity}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-3">
                    {/* Event Payload */}
                    <div className="p-3 rounded-lg bg-muted/50 border">
                      <p className="text-xs text-muted-foreground mb-2">Event Data:</p>
                      {renderPayload(event.payload)}
                    </div>
                    
                    {/* Target Departments */}
                    {event.target_departments && event.target_departments.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Target:</span>
                        {event.target_departments.map(dept => (
                          <Badge key={dept} variant="secondary" className="text-xs">
                            {dept}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2">
                      {event.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeEvent(event.event_id)}
                            data-testid={`acknowledge-btn-${event.event_id}`}
                          >
                            <Clock className="w-4 h-4 mr-2" />
                            Acknowledge
                          </Button>
                          
                          <Button
                            size="sm"
                            onClick={() => completeEvent(event.event_id)}
                            data-testid={`complete-btn-${event.event_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark Complete
                          </Button>
                        </>
                      )}
                      
                      {event.status === 'acknowledged' && (
                        <Button
                          size="sm"
                          onClick={() => completeEvent(event.event_id)}
                          data-testid={`complete-btn-${event.event_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark Complete
                        </Button>
                      )}
                      
                      {event.status === 'completed' && event.completed_at && (
                        <p className="text-xs text-muted-foreground">
                          ✅ Completed {formatTimeAgo(event.completed_at)} by {event.completed_by}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Event Bus Info */}
        <Alert className="border-primary/20 bg-primary/5">
          <Sparkles className="w-4 h-4 text-primary" />
          <AlertDescription className="text-sm">
            <strong>Event Bus Status:</strong> Active (MongoDB Change Streams) • 
            Auto-refresh: {autoRefresh ? 'Every 30s' : 'Manual'} • 
            {stats && (
              <>
                Total Events (7d): {stats.total_events} • 
                By Agent: Bitsy ({stats.by_agent?.bitsy || 0}), 
                Marina ({stats.by_agent?.marina || 0}), 
                Sage ({stats.by_agent?.sage || 0})
              </>
            )}
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}

// ─── UTILITY FUNCTIONS ───

function formatEventType(eventType) {
  return eventType
    .split('.')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' › ');
}

function formatTimeAgo(isoString) {
  if (!isoString) return 'unknown';
  
  const date = new Date(isoString);
  const seconds = Math.floor((new Date() - date) / 1000);
  
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function renderPayload(payload) {
  if (!payload || Object.keys(payload).length === 0) {
    return <p className="text-xs text-muted-foreground italic">No additional data</p>;
  }
  
  return (
    <div className="space-y-1">
      {Object.entries(payload).map(([key, value]) => (
        <div key={key} className="flex items-start gap-2 text-xs">
          <span className="text-muted-foreground min-w-[120px]">{key}:</span>
          <span className="font-medium flex-1 break-all">
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </span>
        </div>
      ))}
    </div>
  );
}
